import { useEffect, useState } from 'react';
import { Alert, Divider, LinkWithIcon, CustomText, OtpInput } from '@vfit/shared/atoms';
import { getClickToCallWithKeyOrUrl, LoggerInstance, trackLink } from '@vfit/shared/data-access';
import { AlertSuccess, AlertWarning } from '@vfit/shared-icons';
import {
  Container,
  Description,
  Detail,
  ErrorMessage,
  LinkContainer,
  LinkWithIconContainer,
  TimerContainer,
  Title,
} from './otp.style';
import { IOtpProps } from './otp.models';
import GenericLoading from '../GenericLoading/genericLoading';
import { useForm } from 'react-hook-form';
import { otpFormSchema } from './otp.utils';
import { useQueryClient } from 'react-query';
import { purify } from '@vfit/shared/themes';

const Otp = ({
  configOtp,
  contactMedium,
  isLoading,
  isError,
  reSendOtp,
  onVerify,
  setIsError,
  noAccessEnabled = true,
  product,
  showLoaderCard,
  isSuccessVerifyOtp,
  isMobileFlow,
  isTimerActive,
}: IOtpProps) => {
  const [otp, setOtp] = useState<string | null>(null);
  const client = useQueryClient();
  const [hasSentNewCode, setHasSentNewCode] = useState(false);
  const [isInError, setIsInError] = useState(false);
  const { register, watch, reset } = useForm({
    mode: 'onSubmit',
  });
  const otpInserted = watch('otp');
  const {
    otpError,
    title,
    links,
    detail,
    alerts,
    instructionText,
    codeResponseDescription,
    timer,
  } = configOtp;

  const [pTimerLabel] = purify([timer?.timerLabel]);

  const handleReSend = () => {
    setHasSentNewCode(true);
    setIsError();
    if (contactMedium) reSendOtp(contactMedium.field, contactMedium.fieldType);
    setTimeout(() => setHasSentNewCode(false), 30000);
  };

  const handleChange = (otp: string) => {
    onVerify(otp);
  };

  const handleRedirect = () => {
    if (product?.urlClickToCallOtp) {
      getClickToCallWithKeyOrUrl(client, product?.urlClickToCallOtp, product);
    } else if (links?.urlNoAccess) {
      window.location.href = links.urlNoAccess;
    }
  };

  useEffect(() => {
    if (isInError) {
      reset();
      setOtp(null);
      setIsInError(false);
    }
  }, [isInError]);

  useEffect(() => {
    if (isError) {
      setHasSentNewCode(false);
      setIsInError(true);
    }
  }, [isError]);

  useEffect(() => {
    if (otpInserted?.length == 6) {
      handleChange(otpInserted);
    }
  }, [otpInserted]);

  useEffect(() => {
    if (/iPhone/.test(navigator.userAgent)) {
      LoggerInstance.debug('Iphone user');
    } else {
      const requestOTP = async () => {
        if ('OTPCredential' in window) {
          try {
            const otpFromNavigator = await (navigator as any).credentials.get({
              otp: { transport: ['sms'] },
              signal: new AbortController().signal,
            });
            setOtp(otpFromNavigator.code);
          } catch (err) {
            LoggerInstance.debug('Failed to get OTP');
          }
        }
      };
      requestOTP();
    }
  }, []);

  if (showLoaderCard && product) return <GenericLoading product={product} />;

  return (
    <Container>
      <Title>{title}</Title>
      <Description>{codeResponseDescription}</Description>
      <CustomText
        text={instructionText.text || ''}
        fontFamily={instructionText.fontFamily}
        size={instructionText.size}
        lineHeight={instructionText.lineHeight}
      />
      {!isError && <Divider marginBottom={-1.5} />}
      {isError && (
        <div style={{ marginTop: '-8px', marginBottom: '24px' }}>
          <Alert type="error" icon={<AlertWarning />} text={alerts?.error || ''} />
        </div>
      )}
      <div style={{ paddingTop: '24px' }}>
        {!isInError && (
          <OtpInput
            style={{
              paddingBottom: 0,
            }}
            inputMode="numeric"
            autoFocus
            data-cs-mask=""
            disabled={isLoading || isSuccessVerifyOtp}
            value={otp || ''}
            {...register('otp', {
              ...otpFormSchema?.otp,
              onChange: (e) => setOtp(e.target.value),
              value: otp || '',
            })}
          />
        )}
        {isError && otpError && <ErrorMessage>{otpError}</ErrorMessage>}
      </div>
      <Detail>{detail}</Detail>
      <LinkContainer isMobileStyle={isMobileFlow}>
        <LinkWithIconContainer disabled={isLoading || isSuccessVerifyOtp || isTimerActive}>
          <LinkWithIcon
            text={links.sendNew}
            disabled={isLoading || isSuccessVerifyOtp || isTimerActive}
            onClick={() => {
              trackLink(links.sendNew || '');
              handleReSend();
            }}
          />
        </LinkWithIconContainer>
        {isMobileFlow && (
          <TimerContainer>
            {isTimerActive && pTimerLabel && (
              <span
                dangerouslySetInnerHTML={{
                  __html: pTimerLabel,
                }}
              />
            )}
          </TimerContainer>
        )}
        {noAccessEnabled && (
          <LinkWithIcon
            text={links.noAccess}
            onClick={() => {
              trackLink(links.noAccess || '');
              handleRedirect();
            }}
          />
        )}
      </LinkContainer>
      {hasSentNewCode && (
        <div style={{ marginTop: '28px' }}>
          <Alert type="success" icon={<AlertSuccess />} text={alerts.sentNew || ''} />
        </div>
      )}
    </Container>
  );
};

export default Otp;

